import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { getIn, setIn, useFormik } from 'formik';
import queryString from 'query-string';

import Filter from 'Lib/components/common/searchable-list/Filter';
import useFilter from 'Lib/components/common/searchable-list/filters/useFilter';
import TempFilter from 'Lib/components/common/searchable-list/TempFilter';
import filterParameters from 'Lib/utilities/filterParameters';
import isEmpty from 'Lib/utilities/isEmpty';
import isNil from 'Lib/utilities/isNil';
import shallowEqual from 'Lib/utilities/shallowEqual';

import SavedSearches from '../searchable-list/SavedSearches';
import Filters from '../table/controls/Filters';

import 'Lib/components/common/searchable-list/styles.scss';

const FiltersList = props => {
    const history = useHistory();
    const dispatch = useDispatch();
    const drawerContentTypeID = useSelector(state => state.ui.drawerContentTypeID);

    const { apis, listTypeID, filters = {}, config = {}, reduxData, showRefresh = true, handler, clearField } = props;

    const [isLoading, setIsLoading] = useState(true);
    const [values, setValues] = useState(props.defaultFilters);
    const [tempFilters, setTempFilters] = useState(props.tempFilters);
    const [moreFilters, setMoreFilters] = useState([]);
    const [showFilters, setShowFilters] = useState(true);
    const [rerenderCount, setRerenderCount] = useState(0);
    const [filterRerenderCount, setFilterRenderCount] = useState(0);

    // Omit page & pageSize from active filters
    const { page, pageSize, ...activeFilters } = queryString.parse(history.location.search);

    // Check for pageSize value in the state (tablePageSize in localStorage)
    const tablePageSize = useSelector(state => state.ui.table.pageSize);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { ...activeFilters },
        onSubmit: formikValues => {
            fetchSearch(formikValues);
            dispatch(props.actions.setDrawerOpen(false));
        }
    });

    /* Rescaffolds formik values when the form is cleared, closed, and reopened */
    useEffect(() => {
        formik.setValues(activeFilters);
    }, [drawerContentTypeID]);

    useEffect(() => {
        const { parsed } = filterParameters();
        const filterMap = {};
        let { page, pageSize, ...initialSearchFilters } = parsed;

        if (Array.isArray(filters.buttons)) {
            filters.buttons.forEach(filter => {
                filterMap[filter.name] = filter;
            });
        }

        if (!isEmpty(initialSearchFilters)) {
            initialSearchFilters = parsed;
        } else {
            initialSearchFilters = props.defaultFilters;
        }

        setValues(initialSearchFilters);

        /*
			Since we now set our filter values in the query string, the query string can contain a filter value that is
			'hidden' inside of the 'More Filters' dropdown. If a user were to refresh with that value, the filter would be
			hidden by default and the user would likely be unaware of what was affecting their search results. The logic below
			checks that scenario:
				* Loop through current keys from our query string
				* Loop through all filters that are 'hidden' in the 'More Filters' section
				* If a key exists within the query string & matches the current hidden filter name, push
				* Call `setMoreFilters()` will all of the filters that were previously hidden to show
		*/
        if (props.filters?.moreButtons) {
            if (!isEmpty(Object.keys(initialSearchFilters))) {
                const hiddenFiltersWithValues =
                    Object.keys(initialSearchFilters) ??
                    [].flatMap(key =>
                        props.filters?.moreButtons.reduce((accumulator, current) => {
                            if (current.name == key) accumulator.push(current);

                            return accumulator;
                        }, [])
                    );
                setMoreFilters(hiddenFiltersWithValues);
            }
        }

        fetchSearch(initialSearchFilters, page, pageSize, 'replace');
        setIsLoading(false);
    }, []);

    // Fetches list with new filters upon hitting back on browser navigation. Allows for them to go back to the last filtered state of their current list
    useEffect(() => {
        return history.listen(() => {
            if (history.action === 'POP') {
                const { parsed } = filterParameters();

                setValues(parsed);
                // Passing 'replace' as the action to avoid making a push when hitting the back button ('POP') which prevented users from navigating back
                fetchSearch(parsed, parsed.page, parsed.pageSize, 'replace');
            }
        });
    }, [history]);

    const fetchSearch = (params, page = props.page || 1, pageSize = props.pageSize || tablePageSize, action = 'push') => {
        if (handler) {
            const { orderBy } = queryString.parse(history.location.search);

            history[action]({
                search: queryString.stringify({ ...params, page, pageSize, orderBy }, { skipNull: true })
            });

            handler({
                ...params,
                orderBy,
                page,
                pageSize
            });
        }
    };

    const setFilterValue = (name, value, doSearch = true) => {
        let newValues = { ...values };

        if (isNil(value)) {
            delete newValues[name];
        } else {
            newValues = setIn(newValues, name, value);
        }

        if (shallowEqual({ ...values }, newValues)) return;

        setValues(newValues);

        if (doSearch) {
            fetchSearch(newValues);
        }
    };

    const setFilterValues = (bindValues, doSearch = true) => {
        let newValues = { ...values };

        bindValues.forEach(({ name, value }) => {
            if (name) {
                if (!isNil(value)) {
                    newValues = setIn(newValues, name, value);
                } else {
                    delete newValues[name];
                }
            }
        });

        if (shallowEqual({ ...values }, newValues)) return;

        setValues(newValues);

        if (doSearch) {
            fetchSearch(newValues);
        }
    };

    const getFilterValue = (name, defaultValue) => getIn(values, name, defaultValue);

    const getContext = () => ({
        config,
        values,
        filters,
        reduxData,
        getFilterValue,
        setFilterValue,
        setFilterValues,
        removeMoreFilter,
        removeTempFilter,
        addMoreFilter,
        refresh: handleRefreshClick,
        clearField
    });

    const addMoreFilter = filter => {
        const foundFilter = moreFilters.find(moreFilter => moreFilter.name == filter.name);
        if (foundFilter) return;

        const newMoreFilters = [...moreFilters, filter];

        setMoreFilters(newMoreFilters);
    };

    const removeMoreFilter = (name, doSearch = true) => {
        const newValues = { ...values };
        const foundIndex = moreFilters.findIndex(filter => filter.name == name);
        if (foundIndex < 0) return;

        const newMoreFilters = [...moreFilters];
        newMoreFilters.splice(foundIndex, 1);
        // Remove filter value when "More Filter" is removed from view
        delete newValues[name];

        setValues(newValues);
        setMoreFilters(newMoreFilters);

        if (doSearch) {
            fetchSearch(newValues);
        }
    };

    const removeTempFilter = name => {
        const foundIndex = tempFilters.findIndex(filter => filter.name == name);
        if (foundIndex < 0) return;

        const newTempFilters = [...tempFilters];

        newTempFilters.splice(foundIndex, 1);

        setTempFilters(newTempFilters);

        const newValues = { ...values };

        if (newValues[name]) {
            delete newValues[name];
        }

        if (shallowEqual({ ...values }, newValues)) return;

        setValues(newValues);
        fetchSearch(newValues);
    };

    const handleRefreshClick = () => {
        fetchSearch(values);
    };

    const toggleShowFilters = () => setShowFilters(prevState => !prevState);

    return props.mui ? (
        <Filters
            activeFilters={activeFilters}
            actions={props.actions}
            api={props.api}
            filters={filters}
            formik={formik}
            filterRerenderCount={filterRerenderCount}
            setFilterRenderCount={setFilterRenderCount}
        />
    ) : (
        <div className="searchable-list filter-header">
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    flexGrow: '1',
                    width: '100% !important'
                }}
            >
                <div className="searchable-list-filters" style={{ flexGrow: '1' }}>
                    {/*
						isLoading || rerenderCount triggers a re-render. There were issues with filters binding the `getContext()` method too early and not reflecting their values.
						This should probably be reworked eventually and change how we're calling out `getContext()` function to avoid strange behavior, at least I think that's currently the culprit
					*/}
                    {showFilters && (
                        <ul className="searchable-list-filters-list" key={isLoading || rerenderCount}>
                            {filters.buttons?.map((filter, filterIndex) =>
                                !filter.hide ? (
                                    <li key={filterIndex} className="searchable-list-filters-list-item">
                                        <Filter {...filter} context={getContext()} />
                                    </li>
                                ) : null
                            )}
                            {moreFilters && moreFilters.length > 0
                                ? moreFilters.map((filter, filterIndex) => (
                                      <li key={`${filter.name}_${filterIndex}`} className="searchable-list-filters-list-item">
                                          <Filter {...filter} context={getContext()} isMore />
                                      </li>
                                  ))
                                : null}
                            {tempFilters && tempFilters.length > 0
                                ? tempFilters.map((tempFilter, tempFilterIndex) => (
                                      <li key={`temp_filter_${tempFilterIndex}`}>
                                          <TempFilter {...tempFilter} context={getContext()} />
                                      </li>
                                  ))
                                : null}
                            {filters.moreButtons && filters.moreButtons.length > 0 ? (
                                <li className="searchable-list-filters-list-item searchable-list-filters-list-item-more">
                                    <UncontrolledDropdown>
                                        <DropdownToggle caret outline size="sm" color="link">
                                            More Filters
                                        </DropdownToggle>
                                        <DropdownMenu className="searchable-list-filters-list-item-more-list">
                                            {filters.moreButtons
                                                .filter(filter => {
                                                    const foundFilter = moreFilters.find(moreFilter => filter.name == moreFilter.name);

                                                    return !foundFilter;
                                                })
                                                .map((filter, filterIndex) => (
                                                    <DropdownItem
                                                        key={`${filter.name}_${filterIndex}`}
                                                        onClick={() => {
                                                            addMoreFilter(filter);
                                                        }}
                                                    >
                                                        {filter.label}
                                                    </DropdownItem>
                                                ))}
                                        </DropdownMenu>
                                    </UncontrolledDropdown>
                                </li>
                            ) : null}
                        </ul>
                    )}
                    {!isEmpty(filters) && (
                        <Button className="d-sm-flex d-md-none d-lg-none d-xl-none" outline size="sm" color="info" onClick={toggleShowFilters}>
                            {showFilters ? 'Hide Filters' : 'Show Filters'}
                        </Button>
                    )}
                </div>
                <div
                    className="searchable-list-commands"
                    style={{
                        display: 'flex',
                        width: '100%',
                        flexGrow: '0'
                    }}
                >
                    {listTypeID && <SavedSearches listTypeID={listTypeID} apis={apis} search={fetchSearch} setFilterValues={setValues} setRerenderCount={setRerenderCount} />}
                    {showRefresh && (
                        <Button onClick={handleRefreshClick} size="sm" color="link">
                            <i className="fal fa-sync" />
                            <span className="sr-only">Refresh</span>
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export { useFilter };

export default FiltersList;
